import React from 'react';
import dayjs from 'dayjs';

import type { HoveredPing } from '../types';
import { headingToCompass, formatAccuracy } from '../utils/formatters';
import { GoogleMapsElement, useMapSurfaceColors } from '@components/Maps';

import { css } from '@emotion/css';
import {
  MdBattery0Bar,
  MdBattery1Bar,
  MdBattery2Bar,
  MdBattery3Bar,
  MdBattery4Bar,
  MdBattery5Bar,
  MdBattery6Bar,
  MdBatteryFull,
  MdBatteryChargingFull,
} from 'react-icons/md';

// HELPERS ------------------------------------------------------------------- //

const STOPPED_THRESHOLD_MS = 0.9;

const getDrivingStatus = (speedMs?: number, heading?: number): { label: string; color: string } => {
  if (speedMs == null || speedMs < 0) return { label: 'Unknown', color: 'rgba(255,255,255,0.5)' };
  if (speedMs < STOPPED_THRESHOLD_MS) return { label: 'Stopped', color: '#fbbf24' };

  const mph = Math.round(speedMs * 2.237);
  const compass = headingToCompass(heading);
  const detail = compass ? `${mph} mph ${compass}` : `${mph} mph`;
  return { label: `Driving ${detail}`, color: '#60a5fa' };
};

const getBatteryIcon = (level: number, charging?: boolean) => {
  const pct = Math.round(level * 100);
  const color = pct <= 15 ? '#ef4444' : pct <= 40 ? '#fbbf24' : '#4ade80';

  if (charging) return { Icon: MdBatteryChargingFull, color, pct };
  if (pct >= 95) return { Icon: MdBatteryFull, color, pct };
  if (pct >= 80) return { Icon: MdBattery6Bar, color, pct };
  if (pct >= 65) return { Icon: MdBattery5Bar, color, pct };
  if (pct >= 50) return { Icon: MdBattery4Bar, color, pct };
  if (pct >= 35) return { Icon: MdBattery3Bar, color, pct };
  if (pct >= 20) return { Icon: MdBattery2Bar, color, pct };
  if (pct >= 8) return { Icon: MdBattery1Bar, color, pct };
  return { Icon: MdBattery0Bar, color, pct };
};

// TYPES --------------------------------------------------------------------- //

interface PingTooltipProps {
  ping: HoveredPing;
}

// COMPONENT ----------------------------------------------------------------- //

const PingTooltip = ({ ping }: PingTooltipProps) => {
  const surface = useMapSurfaceColors();
  const cls = useStyles(surface.divider);
  const { location } = ping;

  const time = dayjs(location.time).format('h:mm:ss A');
  const status = getDrivingStatus(location.speed, location.heading);
  const accuracy = formatAccuracy(location.accuracy, 'qualitative');
  const battery = location.battery_level != null ? getBatteryIcon(location.battery_level, location.battery_is_charging) : null;

  // Position tooltip to the right of the dot, with viewport boundary detection
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const [adjustedStyle, setAdjustedStyle] = React.useState<React.CSSProperties>({
    left: ping.screenX + 12,
    top: ping.screenY - 8,
  });

  React.useEffect(() => {
    const el = tooltipRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let left = ping.screenX + 12;
    let top = ping.screenY - 8;
    if (rect.right > window.innerWidth - 8) {
      left = ping.screenX - rect.width - 12;
    }
    if (rect.bottom > window.innerHeight - 8) {
      top = ping.screenY - rect.height + 8;
    }
    setAdjustedStyle({ left, top });
  }, [ping.screenX, ping.screenY]);

  return (
    <GoogleMapsElement
      ref={tooltipRef}
      style={{
        ...adjustedStyle,
        zIndex: 20,
        pointerEvents: 'none',
        padding: '8px 12px',
        borderRadius: 6,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
        whiteSpace: 'nowrap',
        fontFamily: "'Inter Tight', sans-serif",
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      <div className={cls.statusRow}>
        <span className={cls.statusDot} style={{ backgroundColor: status.color }} />
        <span className={cls.statusLabel} style={{ color: status.color }}>{status.label}</span>
      </div>

      <div className={cls.divider} />

      <div className={cls.row}>
        <span className={cls.label} style={{ color: surface.disabled }}>Time</span>
        <span className={cls.value} style={{ color: surface.primary }}>{time}</span>
      </div>
      {accuracy !== '-' ? (
        <div className={cls.row}>
          <span className={cls.label} style={{ color: surface.disabled }}>Signal</span>
          <span className={cls.value} style={{ color: surface.primary }}>{accuracy}</span>
        </div>
      ) : null}
      {battery ? (
        <div className={cls.row}>
          <span className={cls.label} style={{ color: surface.disabled }}>Battery</span>
          <span className={cls.batteryValue}>
            <battery.Icon size={16} style={{ color: battery.color, transform: 'rotate(90deg)' }} />
            <span style={{ color: battery.color }}>{battery.pct}%</span>
          </span>
        </div>
      ) : null}
      {location.move_id ? (
        <div className={cls.row}>
          <span className={cls.label} style={{ color: surface.disabled }}>Move</span>
          <span className={cls.value} style={{ color: surface.primary }}>#{location.move_id}</span>
        </div>
      ) : null}
    </GoogleMapsElement>
  );
};

// STYLES -------------------------------------------------------------------- //

const useStyles = (dividerColor?: string) => ({
  statusRow: css`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 2px;
  `,
  statusDot: css`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  `,
  statusLabel: css`
    font-weight: 700;
    font-size: 13px;
    font-family: 'Inter', sans-serif;
  `,
  divider: css`
    height: 1px;
    background-color: ${dividerColor || 'rgba(255, 255, 255, 0.1)'};
    margin: 4px 0;
  `,
  row: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  label: css`
    font-size: 11px;
    min-width: 48px;
  `,
  value: css`
    font-weight: 600;
    font-size: 12px;
  `,
  batteryValue: css`
    display: flex;
    align-items: center;
    gap: 3px;
    font-weight: 600;
    font-size: 12px;
  `,
});

export default PingTooltip;
